/**
 * Pure helpers for the checkDelivery pre-check flow (Task 11).
 *
 * POST /:orgId/:siteId/checkDelivery
 *   body  { lat, lng }
 *   response:
 *     { deliverable: bool, fee: int (cents), etaMinutes: int,
 *       minOrder: int (cents, 0 = no minimum),
 *       reason?: "INVALID_DELIVERY_ADDRESS"|"ROUTE_NOT_FOUND"|"NO_DELIVERY_AVAILABLE" }
 */

export type DeliveryReasonCode =
  | "INVALID_DELIVERY_ADDRESS"
  | "ROUTE_NOT_FOUND"
  | "NO_DELIVERY_AVAILABLE";

export interface CheckDeliveryResponse {
  deliverable: boolean;
  fee: number;          // cents (our per-site delivery charge — what the customer pays)
  etaMinutes: number;
  minOrder: number;     // cents, 0 = no minimum
  reason?: DeliveryReasonCode;
  // Uber Direct quote metadata — present only for an UBER_DIRECT order type once
  // Uber has confirmed it can service the address. The customer still pays `fee`.
  uberQuoteId?: string;
  quoteExpires?: string; // ISO timestamp
  dropoffEta?: string;   // ISO timestamp
}

/** Maps a backend reason code to a user-facing blocking message. */
export const deliveryReasonToMessage = (reason: DeliveryReasonCode): string => {
  switch (reason) {
    case "INVALID_DELIVERY_ADDRESS":
      return "We couldn't find that address. Please check the address and try again.";
    case "ROUTE_NOT_FOUND":
      return "Unfortunately we don't deliver to this address. It may be too far away, or outside our delivery area.";
    case "NO_DELIVERY_AVAILABLE":
      return "Delivery is not currently available to this address. Please try again later or choose pickup.";
    default:
      // Exhaustive fallback for future reason codes
      return "We're unable to deliver to this address.";
  }
};
